import style from './ProductDefinitionSection.module.scss';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { Button } from '@components/Common';
import { translate2 as translate } from 'utils/translate';
import { useLocale } from '@components/Contexts/LocaleContext';
import { useRouter } from 'next/router';
import { FC } from 'react';

interface Props {
  click: any;
}

const hidden = { opacity: 0, y: 20 };
const visible = { opacity: 1, y: 0 };
const transition = { duration: 1 };

const ProductDefinitionSection: FC<Props> = ({ click }) => {
  const { lang } = useLocale();
  const router = useRouter();

  return (
    <section
      id="e-signature"
      className="relative w-full h-fit pt-[3.5rem] bg-darkBlue"
    >
      {/* background mobile */}
      <div
        className={`absolute top-0 w-screen h-full md:hidden ${style.filterBackground}`}
      >
        <div className="absolute bottom-0 w-full">
          <Image
            src="/images/e-signature/bg2.webp"
            alt="bg"
            width={375}
            height={480}
            sizes="100vw"
            style={{ width: '100%' }}
          />
        </div>
      </div>

      {/* background tablet */}
      <div className="hidden w-screen md:block lg:hidden">
        <Image
          src="/images/e-signature/bg2lg.webp"
          alt="bg"
          width={744}
          height={360}
          sizes="100vw"
          style={{ width: '100%' }}
        />
      </div>

      {/* background desktop */}
      <div className="hidden w-screen lg:block md:hidden">
        <Image
          src="/images/e-signature/bg2xl.webp"
          alt="bg"
          width={1440}
          height={396}
          sizes="100vw"
          style={{ width: '100%' }}
        />
      </div>

      <div className="md:absolute w-full md:h-full top-0 md:pt-[3.5rem] z-10">
        <div className="relative h-full flex flex-col justify-center items-center pt-8 md:pt-0 pb-40 md:pb-0">
          <div className="w-full flex flex-col lg:flex-row lg:gap-6 lg:items-center text-white px-4 md:px-12 lg:px-6 xl:px-[8.875rem] xxl:px-[8.875rem] lg:container lg:mx-auto">
            <div className="md:w-[80%]">
              <motion.h1
                className="text-h4Bold md:text-h2Bold lg:text-h1Bold xxl:text-dSmallBold gradient-head-large py-1"
                initial={hidden}
                animate={visible}
                transition={transition}
              >
                {lang === 'en'
                  ? 'What is e-Signature?'
                  : 'e-Signature คืออะไร?'}
              </motion.h1>

              <div className="w-[160px] md:w-[205px] lg:w-[228px] xxl:w-[250px] my-3 lg:my-2 xl:my-5">
                <motion.div
                  className="bg-[#f0f3f5] h-0.5 rounded"
                  initial={{ ...hidden, width: 0 }}
                  animate={{ ...visible, width: '100%' }}
                  transition={{ ...transition }}
                ></motion.div>
              </div>

              <motion.p
                className={`text-p3Regular md:text-p2Regular lg:text-p3Regular xl:text-p2Regular 2xl:text-p1Regular text-white py-1 md:w-[80%] ${
                  lang === 'en' ? 'lg:w-[80%]' : 'lg:w-full'
                }`}
                initial={hidden}
                animate={visible}
                transition={{ ...transition, delay: 0.25 }}
              >
                {lang === 'en' ? (
                  <>
                    Unlock your organization&apos;s{' '}
                    <span className="font-semibold">
                      digital transformation
                    </span>{' '}
                    with our best-in-class{' '}
                    <span className="font-semibold">e-Signature system.</span>
                    <br />
                    Streamline document signing processes,{' '}
                    <span className="font-semibold">
                      eliminate paper-based transactions,
                    </span>
                    <br />
                    and enhance operational agility. Ensure full compliance with{' '}
                    <span className="font-semibold">
                      the Electronic Transactions Act
                    </span>{' '}
                    <br className='hidden md:block'/>
                    and the <span className="font-semibold">ETDA</span>{' '}
                    standards for secure and legally recognized digital
                    signatures.
                  </>
                ) : (
                  <>
                    เริ่มต้นการทำ{' '}
                    <span className="font-semibold">
                      Digital Transformation
                    </span>{' '}
                    ให้กับองค์กรของคุณด้วย
                    <span className="font-semibold">
                      ระบบลงลายมือชื่ออิเล็กทรอนิกส์{' '}
                      <span className="whitespace-nowrap">(e-Signature)</span>
                    </span>
                    <br />
                    เพิ่มความคล่องตัวในการลงนามบนเอกสาร{' '}
                    <span className="font-semibold">
                      ไม่ต้องใช้กระดาษอีกต่อไป
                    </span>
                    <br />
                    ถูกต้องตาม
                    <span className="font-semibold">
                      พ.ร.บ.ธุรกรรมทางอิเล็กทรอนิกส์
                    </span>{' '}
                    และ
                    <span className="font-semibold">
                      มาตรฐานสำนักงานพัฒนาธุรกรรมทางอิเล็กทรอนิกส์ (ETDA)
                    </span>
                  </>
                )}
              </motion.p>

              <div className="mt-5 lg:mt-3">
                <Button
                  id="contact-us-esignature"
                  icon="/icons/speech.svg"
                  text={translate('ติดต่อเรา', 'CONTACT US', lang)}
                  className="text-sm border border-white"
                  onClick={click}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductDefinitionSection;
